import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { 
  Store, TrendingUp, TrendingDown, Minus, ShoppingBag, Receipt, 
  CreditCard, Banknote, ArrowRight, Clock, AlertTriangle, CheckCircle2,
  BarChart3, Package, Users, Calendar, ChevronRight, RefreshCw, Filter,
  XCircle, Info
} from 'lucide-react';
import { StorePeriodFilter, useStorePeriodFilter, getDateRange } from '@/components/retail/StorePeriodFilter';
import { useStoreMapping } from '@/hooks/useStoreMapping';

// Format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/D';
  return new Intl.NumberFormat('pt-PT', { 
    style: 'currency', 
    currency: 'EUR',
    minimumFractionDigits: 2 
  }).format(value);
};

// Format number
const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined) return 'N/D';
  return new Intl.NumberFormat('pt-PT', { 
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals 
  }).format(value);
};

// Format percentage
const formatPercent = (value) => {
  if (value === null || value === undefined) return 'N/D';
  const sign = value > 0 ? '+' : '';
  return `${sign}${formatNumber(value, 1)}%`;
};

// Variation badge component
const VariationBadge = ({ current, previous, isPercent = false }) => {
  if (previous === null || previous === undefined || previous === 0) {
    return <span className="text-xs text-slate-400">N/D</span>;
  }
  
  const variation = ((current - previous) / Math.abs(previous)) * 100;
  const absVariation = Math.abs(current - previous);
  
  if (Math.abs(variation) < 0.1) {
    return (
      <span className="inline-flex items-center text-xs text-slate-500">
        <Minus className="w-3 h-3 mr-1" /> 0%
      </span>
    );
  }
  
  const isPositive = variation > 0;
  const colorClass = isPositive ? 'text-emerald-600' : 'text-red-600';
  const Icon = isPositive ? TrendingUp : TrendingDown;
  
  return (
    <span className={`inline-flex items-center text-xs ${colorClass}`}>
      <Icon className="w-3 h-3 mr-1" />
      {formatPercent(variation)}
      <span className="ml-1 text-slate-400">({isPositive ? '+' : ''}{formatCurrency(absVariation)})</span>
    </span>
  );
};

// KPI Card component - Enhanced with comparison data
const KPICard = ({ 
  title, 
  value, 
  subtitle, 
  icon: Icon, 
  previous, 
  previousLabel = 'vs período anterior',
  trend, 
  format = 'currency', 
  color = 'slate',
  returnRate,  // Optional return rate percentage
}) => {
  const formattedValue = format === 'currency' ? formatCurrency(value) : 
                         format === 'number' ? formatNumber(value) :
                         format === 'decimal' ? formatNumber(value, 2) :
                         format === 'percent' ? `${formatNumber(value, 1)}%` : value;
  
  const colorClasses = {
    slate: 'bg-slate-50 text-slate-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    red: 'bg-red-50 text-red-600',
    amber: 'bg-amber-50 text-amber-600',
    blue: 'bg-blue-50 text-blue-600',
  };
  
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="pt-4 pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{formattedValue}</p>
            {previous !== undefined && (
              <div className="mt-1">
                <VariationBadge current={value} previous={previous} />
                <span className="text-[10px] text-slate-400 ml-1">{previousLabel}</span>
              </div>
            )}
            {returnRate !== undefined && (
              <p className="text-xs text-red-500 mt-1">
                Taxa: {formatNumber(returnRate, 1)}%
              </p>
            )}
            {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
          </div>
          {Icon && (
            <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
              <Icon className="w-5 h-5" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Note: Period options and date range calculation imported from StorePeriodFilter

// Calculate comparison period (previous equivalent period)
const getComparisonPeriod = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const daysDiff = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
  
  // Previous period: same length, immediately before
  const prevEnd = new Date(startDate);
  prevEnd.setDate(prevEnd.getDate() - 1);
  const prevStart = new Date(prevEnd);
  prevStart.setDate(prevStart.getDate() - daysDiff + 1);
  
  // Year-over-year (homologous period)
  const yoyStart = new Date(startDate);
  yoyStart.setFullYear(yoyStart.getFullYear() - 1);
  const yoyEnd = new Date(endDate);
  yoyEnd.setFullYear(yoyEnd.getFullYear() - 1);
  
  return {
    previous: {
      start: prevStart.toISOString().split('T')[0],
      end: prevEnd.toISOString().split('T')[0],
    },
    yoy: {
      start: yoyStart.toISOString().split('T')[0],
      end: yoyEnd.toISOString().split('T')[0],
    },
  };
};

// Data Quality Status Badge Component
const DataQualityBadge = ({ status, count, showLabel = true }) => {
  const configs = {
    RECONCILED: {
      icon: CheckCircle2,
      color: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      label: 'Reconciliado',
    },
    MISMATCH: {
      icon: XCircle,
      color: 'bg-red-50 text-red-700 border-red-200',
      label: 'Discrepância',
    },
    NO_DATA: {
      icon: Info,
      color: 'bg-slate-100 text-slate-500 border-slate-200',
      label: 'Sem Dados',
    },
    NO_PAYMENT: {
      icon: AlertTriangle,
      color: 'bg-amber-50 text-amber-700 border-amber-200',
      label: 'Sem Pagamento',
    },
  };
  
  const config = configs[status] || configs.NO_DATA;
  const Icon = config.icon;
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.color}`}
         data-testid={`data-quality-badge-${status.toLowerCase()}`}>
      <Icon className="w-3.5 h-3.5" />
      {showLabel && <span>{config.label}</span>}
      {count !== undefined && <span className="font-bold">({count})</span>}
    </div>
  );
};

// Data Quality indicator - Enhanced version
const DataQualityIndicator = ({ reconciled, mismatch, noPayment, noData = false }) => {
  // If no data at all
  if (noData || (reconciled === 0 && mismatch === 0 && noPayment === 0)) {
    return (
      <div className="flex items-center gap-2" data-testid="data-quality-indicator">
        <DataQualityBadge status="NO_DATA" showLabel={true} />
        <span className="text-xs text-slate-400">Nenhum documento importado para este período</span>
      </div>
    );
  }
  
  const total = reconciled + mismatch + noPayment;
  const hasIssues = mismatch > 0 || noPayment > 0;
  
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="data-quality-indicator">
      {/* Main status */}
      {!hasIssues ? (
        <DataQualityBadge status="RECONCILED" count={reconciled} />
      ) : (
        <>
          {reconciled > 0 && <DataQualityBadge status="RECONCILED" count={reconciled} />}
          {mismatch > 0 && <DataQualityBadge status="MISMATCH" count={mismatch} />}
          {noPayment > 0 && <DataQualityBadge status="NO_PAYMENT" count={noPayment} />}
        </>
      )}
      {/* Summary */}
      <span className="text-xs text-slate-500 ml-2">
        {hasIssues 
          ? `${reconciled}/${total} documentos OK` 
          : `Todos os ${total} documentos reconciliados ✓`
        }
      </span>
    </div>
  );
};

// Store comparison table with sorting
const StoreComparisonTable = ({ stores, onStoreClick, getStoreName }) => {
  const [sortField, setSortField] = useState('net_sales');
  const [sortDir, setSortDir] = useState('desc');
  
  if (!stores || stores.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-8">Nenhuma loja com dados</p>;
  }
  
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };
  
  const sortedStores = [...stores].sort((a, b) => {
    let aVal = a[sortField] || 0;
    let bVal = b[sortField] || 0;
    
    // Special handling for calculated fields
    if (sortField === 'returnRate') {
      aVal = a.gross_sales > 0 ? (a.returns_value / a.gross_sales) * 100 : 0;
      bVal = b.gross_sales > 0 ? (b.returns_value / b.gross_sales) * 100 : 0;
    } else if (sortField === 'cashPct') {
      const aTot = a.net_cash + a.net_card + (a.net_other || 0);
      const bTot = b.net_cash + b.net_card + (b.net_other || 0);
      aVal = aTot > 0 ? (a.net_cash / aTot) * 100 : 0;
      bVal = bTot > 0 ? (b.net_cash / bTot) * 100 : 0;
    } else if (sortField === 'cardPct') {
      const aTot = a.net_cash + a.net_card + (a.net_other || 0);
      const bTot = b.net_cash + b.net_card + (b.net_other || 0);
      aVal = aTot > 0 ? (a.net_card / aTot) * 100 : 0;
      bVal = bTot > 0 ? (b.net_card / bTot) * 100 : 0;
    }
    
    return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
  });
  
  const SortHeader = ({ field, children, className = '' }) => (
    <th 
      className={`py-3 px-2 font-medium text-slate-600 cursor-pointer hover:bg-slate-50 select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center justify-end gap-1">
        {children}
        {sortField === field && (
          <span className="text-red-500">{sortDir === 'asc' ? '↑' : '↓'}</span>
        )}
      </div>
    </th>
  );
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            <th className="text-left py-3 px-2 font-medium text-slate-600">Loja</th>
            <SortHeader field="net_sales" className="text-right">Vendas Líq.</SortHeader>
            <SortHeader field="gross_margin" className="text-right">Margem Bruta</SortHeader>
            <SortHeader field="sales_documents" className="text-right hidden sm:table-cell">Transações</SortHeader>
            <SortHeader field="average_ticket" className="text-right hidden md:table-cell">Ticket Médio</SortHeader>
            <SortHeader field="units_sold" className="text-right hidden md:table-cell">Unidades</SortHeader>
            <SortHeader field="returns_value" className="text-right hidden lg:table-cell">Devoluções</SortHeader>
            <SortHeader field="returnRate" className="text-right hidden lg:table-cell">Return %</SortHeader>
            <SortHeader field="cashPct" className="text-right hidden xl:table-cell">Cash %</SortHeader>
            <SortHeader field="cardPct" className="text-right hidden xl:table-cell">Card %</SortHeader>
            <th className="w-10"></th>
          </tr>
        </thead>
        <tbody>
          {sortedStores.map((store, idx) => {
            const totalPayments = store.net_cash + store.net_card + (store.net_other || 0);
            const cashPct = totalPayments > 0 ? (store.net_cash / totalPayments) * 100 : 0;
            const cardPct = totalPayments > 0 ? (store.net_card / totalPayments) * 100 : 0;
            const returnRate = store.gross_sales > 0 ? (store.returns_value / store.gross_sales) * 100 : 0;
            
            return (
              <tr 
                key={store.store_code} 
                className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                onClick={() => onStoreClick(store.store_code)}
              >
                <td className="py-3 px-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${idx === 0 ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                    <span className="font-medium">{getStoreName ? getStoreName(store.store_code) : store.store_code}</span>
                  </div>
                </td>
                <td className="text-right py-3 px-2 font-semibold">{formatCurrency(store.net_sales)}</td>
                <td className="text-right py-3 px-2" data-testid={`store-margin-${store.store_code}`}>
                  {store.gross_margin === undefined || store.gross_margin === null ? (
                    <span className="text-slate-300">—</span>
                  ) : (
                    <>
                      <div className={`font-semibold ${store.gross_margin < 0 ? 'text-red-600' : 'text-emerald-700'}`}>{formatCurrency(store.gross_margin)}</div>
                      <div className="text-[11px] text-slate-500">{store.gross_margin_pct ?? '—'}%</div>
                    </>
                  )}
                </td>
                <td className="text-right py-3 px-2 hidden sm:table-cell">{store.sales_documents}</td>
                <td className="text-right py-3 px-2 hidden md:table-cell">{formatCurrency(store.average_ticket)}</td>
                <td className="text-right py-3 px-2 hidden md:table-cell">{formatNumber(store.units_sold)}</td>
                <td className="text-right py-3 px-2 hidden lg:table-cell text-red-600">{formatCurrency(store.returns_value)}</td>
                <td className="text-right py-3 px-2 hidden lg:table-cell text-red-600">{formatNumber(returnRate, 1)}%</td>
                <td className="text-right py-3 px-2 hidden xl:table-cell">{formatNumber(cashPct, 1)}%</td>
                <td className="text-right py-3 px-2 hidden xl:table-cell">{formatNumber(cardPct, 1)}%</td>
                <td className="py-3 px-2">
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Hourly sales chart (simple bar visualization)
const HourlySalesChart = ({ data }) => {
  if (!data || data.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-8">Sem dados horários</p>;
  }
  
  const maxSales = Math.max(...data.map(h => h.sales || 0));
  
  return (
    <div className="space-y-2">
      {data.map((hour) => {
        const pct = maxSales > 0 ? ((hour.sales || 0) / maxSales) * 100 : 0;
        return (
          <div key={hour._id} className="flex items-center gap-3">
            <span className="w-8 text-xs text-slate-500 text-right">{hour._id}h</span>
            <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-red-500 to-red-600 rounded-full transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className="w-20 text-xs text-slate-600 text-right">{formatCurrency(hour.sales)}</span>
            <span className="w-12 text-xs text-slate-400 text-right">{hour.transactions} tx</span>
          </div>
        );
      })}
    </div>
  );
};

// Top SKUs table
const TopSKUsTable = ({ skus, title = "Top Produtos" }) => {
  if (!skus || skus.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-4">Sem dados</p>;
  }
  
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-slate-700">{title}</h4>
      <div className="space-y-1">
        {skus.slice(0, 10).map((sku, idx) => (
          <div key={sku._id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-slate-50">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-slate-100 text-xs flex items-center justify-center text-slate-600">
                {idx + 1}
              </span>
              <span className="text-sm font-medium text-slate-700 truncate max-w-[150px]">{sku._id}</span>
            </div>
            <div className="text-right">
              <span className="text-sm font-semibold">{formatCurrency(sku.revenue)}</span>
              <span className="text-xs text-slate-400 ml-2">{formatNumber(sku.quantity)} un</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Enhanced Daily Sales Bar Chart with cumulative line
const DailySalesBarChart = ({ data, onDayClick }) => {
  if (!data || data.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-4">Sem dados diários</p>;
  }
  
  // Data is already sorted chronologically from backend
  const displayData = data;
  const maxSales = Math.max(...displayData.map(d => d.net_sales || 0));
  
  // Calculate cumulative
  let cumulative = 0;
  const dataWithCumulative = displayData.map(d => {
    cumulative += (d.net_sales || 0);
    return { ...d, cumulative };
  });
  const maxCumulative = cumulative;
  
  // Format short date for x-axis
  const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    return `${parts[2]}/${parts[1]}`;  // DD/MM
  };
  
  // Chart dimensions
  const chartHeight = 160;
  const chartPadding = 32; // For x-axis labels
  
  return (
    <div className="space-y-3">
      {/* Chart container - bars + cumulative line */}
      <div className="relative h-56 overflow-x-auto" data-testid="daily-sales-chart">
        {/* SVG for cumulative line */}
        <svg 
          className="absolute inset-0 w-full h-48 pointer-events-none"
          viewBox={`0 0 ${Math.max(displayData.length * 28, 200)} ${chartHeight}`}
          preserveAspectRatio="none"
        >
          {/* Cumulative line */}
          <polyline
            fill="none"
            stroke="#6366f1"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            points={dataWithCumulative.map((d, i) => {
              const x = (i + 0.5) * (100 / displayData.length) * displayData.length * 0.28;
              const y = chartHeight - (maxCumulative > 0 ? (d.cumulative / maxCumulative) * (chartHeight - 20) : 0) - 10;
              return `${x},${y}`;
            }).join(' ')}
          />
        </svg>
        
        {/* Bars */}
        <div className="h-48 flex items-end gap-1 pb-6 pt-2 relative z-10">
          {dataWithCumulative.map((day, idx) => {
            const heightPct = maxSales > 0 ? (day.net_sales / maxSales) * 100 : 0;
            const hasReturns = day.returns_value > 0;
            const heightPx = Math.max(Math.round((heightPct / 100) * 130), 4);
            
            return (
              <div 
                key={day.business_date} 
                className="flex-1 min-w-[24px] max-w-[40px] flex flex-col items-center justify-end group cursor-pointer h-full"
                onClick={() => onDayClick && onDayClick(day)}
                title={`${day.business_date}\nVendas: ${formatCurrency(day.net_sales)}\nAcumulado: ${formatCurrency(day.cumulative)}`}
              >
                {/* Bar */}
                <div 
                  className={`w-3/4 rounded-t transition-colors ${
                    hasReturns 
                      ? 'bg-gradient-to-t from-red-400 to-emerald-400' 
                      : 'bg-emerald-500 group-hover:bg-emerald-600'
                  }`}
                  style={{ height: `${heightPx}px`, minHeight: '4px' }}
                />
                
                {/* Label */}
                <span className="text-[9px] text-slate-400 mt-1 transform -rotate-45 origin-top-left whitespace-nowrap">
                  {formatShortDate(day.business_date)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 text-xs text-slate-500 flex-wrap">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-emerald-500" />
          <span>Vendas Líquidas</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-gradient-to-t from-red-400 to-emerald-400" />
          <span>Com Devoluções</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-6 h-0.5 bg-indigo-500" />
          <span>Acumulado</span>
        </div>
      </div>
    </div>
  );
};

// Enhanced Daily Sales Summary Table with cumulative
const DailySalesSummaryTable = ({ data, maxRows = 10, onDayClick }) => {
  if (!data || data.length === 0) {
    return <p className="text-sm text-slate-500 text-center py-4">Sem dados diários</p>;
  }
  
  // Calculate cumulative (chronological order)
  let cumulative = 0;
  const dataWithCumulative = data.map(d => {
    cumulative += (d.net_sales || 0);
    return { ...d, cumulative };
  });
  
  // Show most recent days first for display (but keep cumulative correct)
  const displayData = [...dataWithCumulative].reverse().slice(0, maxRows);
  
  const formatShortDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    return `${parts[2]}/${parts[1]}`;
  };
  
  return (
    <div className="space-y-1 max-h-[280px] overflow-y-auto" data-testid="daily-sales-table">
      {displayData.map((day) => (
        <div 
          key={day.business_date} 
          className="flex items-center justify-between py-2 px-2 rounded hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0"
          onClick={() => onDayClick && onDayClick(day)}
        >
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-slate-700 w-12">{formatShortDate(day.business_date)}</span>
            <span className="text-xs text-slate-500">{day.sales_documents} tx</span>
          </div>
          <div className="flex items-center gap-3">
            {day.returns_value > 0 && (
              <span className="text-xs text-red-500">-{formatCurrency(day.returns_value)}</span>
            )}
            <span className="font-semibold text-emerald-600 w-20 text-right">{formatCurrency(day.net_sales)}</span>
            <span className="text-xs text-indigo-600 w-20 text-right">{formatCurrency(day.cumulative)}</span>
            <ChevronRight className="w-4 h-4 text-slate-300" />
          </div>
        </div>
      ))}
    </div>
  );
};

// Payment mix visualization
const PaymentMixChart = ({ cash, card, other, total }) => {
  if (total <= 0) {
    return <p className="text-sm text-slate-500 text-center">Sem pagamentos</p>;
  }
  
  const cashPct = (cash / total) * 100;
  const cardPct = (card / total) * 100;
  const otherPct = (other / total) * 100;
  
  return (
    <div className="space-y-3">
      {/* Bar */}
      <div className="h-4 rounded-full overflow-hidden flex bg-slate-100">
        {cashPct > 0 && (
          <div 
            className="bg-emerald-500 transition-all" 
            style={{ width: `${cashPct}%` }}
            title={`Numerário: ${formatPercent(cashPct)}`}
          />
        )}
        {cardPct > 0 && (
          <div 
            className="bg-blue-500 transition-all" 
            style={{ width: `${cardPct}%` }}
            title={`Cartão: ${formatPercent(cardPct)}`}
          />
        )}
        {otherPct > 0 && (
          <div 
            className="bg-amber-500 transition-all" 
            style={{ width: `${otherPct}%` }}
            title={`Outros: ${formatPercent(otherPct)}`}
          />
        )}
      </div>
      
      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-emerald-500" />
          <span className="text-slate-600">Numerário</span>
          <span className="font-semibold">{formatCurrency(cash)}</span>
          <span className="text-slate-400">({formatNumber(cashPct, 1)}%)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500" />
          <span className="text-slate-600">Cartão</span>
          <span className="font-semibold">{formatCurrency(card)}</span>
          <span className="text-slate-400">({formatNumber(cardPct, 1)}%)</span>
        </div>
        {other > 0 && (
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-amber-500" />
            <span className="text-slate-600">Outros</span>
            <span className="font-semibold">{formatCurrency(other)}</span>
            <span className="text-slate-400">({formatNumber(otherPct, 1)}%)</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Dashboard Component
const RetailDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Store name mapping hook
  const { getStoreName } = useStoreMapping();
  
  // Use shared filter hook - default to 'this_month' for all retail pages
  const {
    selectedStore,
    setSelectedStore,
    period,
    setPeriod,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    startDate,
    endDate,
    filterProps,
  } = useStorePeriodFilter({ defaultPeriod: 'this_month', defaultStore: 'all' });
  
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data
  const [metrics, setMetrics] = useState(null);
  const [hourlyData, setHourlyData] = useState([]);
  const [topSkus, setTopSkus] = useState([]);
  const [storeMetrics, setStoreMetrics] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [previousMetrics, setPreviousMetrics] = useState(null);  // For YoY/MoM comparison
  const [hasData, setHasData] = useState(true);  // Track if there's data
  const [dailySalesData, setDailySalesData] = useState([]);  // Daily sales for bar chart
  const [vatMode, setVatMode] = useState(() => localStorage.getItem('retail_vat_mode') || 'excl');
  const changeVatMode = (mode) => { setVatMode(mode); localStorage.setItem('retail_vat_mode', mode); };
  const [marginData, setMarginData] = useState(null);

  // Gross margin per store (ex-VAT sales vs SAGE cost) for the same period
  useEffect(() => {
    if (!startDate || !endDate) return;
    setMarginData(null);
    axios.get(`${API}/retail-costs/period`, { params: { start_date: startDate, end_date: endDate } })
      .then((res) => setMarginData(res.data))
      .catch(() => setMarginData(null));
  }, [startDate, endDate]);

  const marginByStore = useMemo(() => {
    const map = {};
    (marginData?.stores || []).forEach((s) => { if (s.store_code) map[s.store_code] = s; });
    return map;
  }, [marginData]);

  const selectedMargin = useMemo(() => {
    if (!marginData || !marginData.last_sync_at) return null;
    return selectedStore === 'all' ? marginData : marginByStore[selectedStore] || null;
  }, [marginData, marginByStore, selectedStore]);

  const storeMetricsWithMargin = useMemo(() => storeMetrics.map((s) => ({
    ...s,
    gross_margin: marginByStore[s.store_code]?.gross_margin,
    gross_margin_pct: marginByStore[s.store_code]?.gross_margin_pct,
  })), [storeMetrics, marginByStore]);
  
  // Calculate return rate
  const returnRate = useMemo(() => {
    if (!metrics || !metrics.gross_sales || metrics.gross_sales === 0) return null;
    return (metrics.returns_value / metrics.gross_sales) * 100;
  }, [metrics]);
  
  // Load data when filters change
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      setPreviousMetrics(null);
      setHasData(true);
      setDailySalesData([]);
      
      const start = startDate;
      const end = endDate;
      const comparisonPeriod = getComparisonPeriod(start, end);
      
      try {
        // Load daily aggregated sales data for bar chart (shared endpoint)
        const dailyParams = new URLSearchParams();
        dailyParams.set('start_date', start);
        dailyParams.set('end_date', end);
        dailyParams.set('vat', vatMode);
        if (selectedStore && selectedStore !== 'all') {
          dailyParams.set('store_code', selectedStore);
        }
        
        const dailyRes = await axios.get(`${API}/retail-analytics/daily-aggregated?${dailyParams.toString()}`).catch(() => ({ data: { days: [] } }));
        // Sort ascending for chart display (oldest to newest)
        const sortedDailyData = (dailyRes.data.days || []).slice().sort((a, b) => a.business_date.localeCompare(b.business_date));
        setDailySalesData(sortedDailyData);
        
        if (selectedStore === 'all') {
          // Load aggregated data for all stores
          const [currentRes, previousRes] = await Promise.all([
            axios.get(`${API}/retail-analytics/dashboard/summary`, {
              params: { start_date: start, end_date: end, vat: vatMode }
            }),
            // Load comparison period data
            axios.get(`${API}/retail-analytics/dashboard/summary`, {
              params: { start_date: comparisonPeriod.previous.start, end_date: comparisonPeriod.previous.end, vat: vatMode }
            }).catch(() => ({ data: { totals: null } }))
          ]);
          
          setMetrics(currentRes.data.totals);
          setStoreMetrics(currentRes.data.by_store || []);
          setHourlyData(currentRes.data.hourly || []);
          setTopSkus(currentRes.data.top_skus || []);
          setPaymentSummary(currentRes.data.payment_summary);
          
          // Set previous period metrics for comparison
          if (previousRes.data?.totals && previousRes.data.totals.net_sales > 0) {
            setPreviousMetrics(previousRes.data.totals);
          }
          
          // Check if we have data
          if (!currentRes.data.totals || currentRes.data.totals.transaction_count === 0) {
            setHasData(false);
          }
        } else {
          // Load single store data
          if (start === end) {
            const [metricsRes, hourlyRes, skusRes, paymentsRes] = await Promise.all([
              axios.get(`${API}/retail-analytics/metrics/${selectedStore}/${start}?vat=${vatMode}`).catch(() => ({ data: null })),
              axios.get(`${API}/retail-analytics/hourly/${selectedStore}/${start}?vat=${vatMode}`).catch(() => ({ data: { hourly: [] } })),
              axios.get(`${API}/retail-analytics/top-skus/${selectedStore}/${start}?vat=${vatMode}`).catch(() => ({ data: { top_skus: [] } })),
              axios.get(`${API}/retail-analytics/payment-summary/${selectedStore}/${start}`).catch(() => ({ data: null })),
            ]);
            
            setMetrics(metricsRes.data);
            setHourlyData(hourlyRes.data?.hourly || []);
            setTopSkus(skusRes.data?.top_skus || []);
            setPaymentSummary(paymentsRes.data);
            setStoreMetrics([]);
            
            // Load previous day for comparison
            const prevDate = comparisonPeriod.previous.start;
            const prevRes = await axios.get(`${API}/retail-analytics/metrics/${selectedStore}/${prevDate}?vat=${vatMode}`).catch(() => ({ data: null }));
            if (prevRes.data && prevRes.data.net_sales > 0) {
              setPreviousMetrics(prevRes.data);
            }
            
            if (!metricsRes.data || metricsRes.data.transaction_count === 0) {
              setHasData(false);
            }
          } else {
            // For range, use summary endpoint with store filter
            const [currentRes, previousRes] = await Promise.all([
              axios.get(`${API}/retail-analytics/dashboard/summary`, {
                params: { start_date: start, end_date: end, store_code: selectedStore, vat: vatMode }
              }),
              axios.get(`${API}/retail-analytics/dashboard/summary`, {
                params: { start_date: comparisonPeriod.previous.start, end_date: comparisonPeriod.previous.end, store_code: selectedStore, vat: vatMode }
              }).catch(() => ({ data: { totals: null } }))
            ]);
            
            setMetrics(currentRes.data.totals);
            setHourlyData(currentRes.data.hourly || []);
            setTopSkus(currentRes.data.top_skus || []);
            setPaymentSummary(currentRes.data.payment_summary);
            setStoreMetrics([]);
            
            if (previousRes.data?.totals && previousRes.data.totals.net_sales > 0) {
              setPreviousMetrics(previousRes.data.totals);
            }
            
            if (!currentRes.data.totals || currentRes.data.totals.transaction_count === 0) {
              setHasData(false);
            }
          }
        }
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError('Erro ao carregar dados. Verifique se existem dados para o período selecionado.');
        setHasData(false);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [selectedStore, startDate, endDate, vatMode]);
  
  const handleStoreClick = (storeCode) => {
    setSelectedStore(storeCode);
  };
  
  // Drill-down to vendas page filtered by specific day
  const handleDayDrillDown = (day) => {
    const params = new URLSearchParams();
    if (selectedStore && selectedStore !== 'all') {
      params.set('store', selectedStore);
    }
    params.set('period', 'custom');
    params.set('start', day.business_date);
    params.set('end', day.business_date);
    navigate(`/lojas/vendas?${params.toString()}`);
  };
  
  const handleRefresh = () => {
    // Trigger reload by forcing a re-render
    setLoading(true);
    window.location.reload();
  };
  
  return (
    <div className="min-h-screen bg-slate-50 pb-8" data-testid="retail-dashboard">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Store className="h-5 w-5 md:h-6 md:w-6 text-red-600" />
                Dashboard Lojas
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {selectedStore === 'all' ? 'Visão geral de todas as lojas' : `Loja: ${getStoreName(selectedStore)}`}
                {' · '}
                <span className="font-medium" data-testid="vat-mode-label">
                  Vendas {vatMode === 'excl' ? 'sem IVA' : 'com IVA'} · pagamentos com IVA
                </span>
              </p>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50" data-testid="vat-mode-toggle">
                {[['excl', 'Sem IVA'], ['incl', 'Com IVA']].map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => changeVatMode(mode)}
                    data-testid={`vat-mode-${mode}`}
                    className={`px-3 py-1.5 text-sm rounded-md transition-colors ${vatMode === mode ? 'bg-white shadow-sm font-semibold text-slate-900' : 'text-slate-500 hover:text-slate-800'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {/* Shared Store/Period Filter */}
              <StorePeriodFilter
                {...filterProps}
                onRefresh={handleRefresh}
                loading={loading}
                showAllStores={true}
              />
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4 md:py-6 space-y-6">
      
      {/* Data Quality Indicator */}
      {metrics && (
        <DataQualityIndicator 
          reconciled={metrics.documents_reconciled || 0}
          mismatch={metrics.documents_mismatch || 0}
          noPayment={metrics.documents_no_payment || 0}
          noData={!hasData}
        />
      )}
      
      {/* Error state */}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-4">
            <p className="text-red-700">{error}</p>
          </CardContent>
        </Card>
      )}
      
      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-slate-400 animate-spin" />
        </div>
      )}
      
      {/* Main content */}
      {!loading && metrics && hasData && (
        <>
          {/* KPI Grid - Enhanced with comparison */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="kpi-grid">
            <KPICard 
              title="Vendas Líquidas" 
              value={metrics.net_sales} 
              icon={ShoppingBag}
              color="emerald"
              previous={previousMetrics?.net_sales}
              previousLabel="vs período ant."
            />
            <KPICard 
              title="Margem Bruta" 
              value={selectedMargin?.gross_margin}
              icon={TrendingUp}
              color="emerald"
              subtitle={selectedMargin
                ? `${selectedMargin.gross_margin_pct ?? '—'}% · vendas s/IVA − custo SAGE`
                : 'Sem dados SAGE (sincronize em Custos)'}
            />
            <KPICard 
              title="Custo das Vendas" 
              value={selectedMargin?.cost_of_sales}
              icon={Package}
              color="amber"
              subtitle="ISL − IEL (custo SAGE)"
            />
            <KPICard 
              title="Vendas Brutas" 
              value={metrics.gross_sales} 
              icon={ShoppingBag}
              color="slate"
              previous={previousMetrics?.gross_sales}
            />
            <KPICard 
              title="Descontos" 
              value={metrics.discounts_total || 0} 
              icon={Receipt}
              color="amber"
              previous={previousMetrics?.discounts_total}
            />
            <KPICard 
              title="Devoluções" 
              value={metrics.returns_value} 
              icon={Receipt}
              color="red"
              returnRate={returnRate}
              previous={previousMetrics?.returns_value}
            />
            <KPICard 
              title="Transações" 
              value={metrics.sales_documents} 
              format="number"
              icon={Receipt}
              color="blue"
              previous={previousMetrics?.sales_documents}
            />
            <KPICard 
              title="Ticket Médio" 
              value={metrics.average_ticket} 
              icon={TrendingUp}
              color="slate"
              previous={previousMetrics?.average_ticket}
            />
            <KPICard 
              title="Unidades" 
              value={metrics.units_sold} 
              format="number"
              icon={Package}
              color="slate"
              previous={previousMetrics?.units_sold}
            />
            <KPICard 
              title="SKUs" 
              value={metrics.distinct_skus} 
              format="number"
              icon={Package}
              color="slate"
            />
          </div>
          
          {/* Payment KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KPICard 
              title="Numerário Líquido" 
              value={metrics.net_cash} 
              subtitle={`Recebido: ${formatCurrency(metrics.cash_received)} | Devolvido: ${formatCurrency(metrics.cash_refunded)}`}
              icon={Banknote}
              color="emerald"
            />
            <KPICard 
              title="Multibanco Líquido" 
              value={metrics.net_card} 
              subtitle={`Recebido: ${formatCurrency(metrics.card_received)} | Devolvido: ${formatCurrency(metrics.card_refunded)}`}
              icon={CreditCard}
              color="blue"
            />
            <KPICard 
              title="Outros Pagamentos" 
              value={metrics.net_other || 0} 
              icon={Receipt}
              color="amber"
            />
          </div>
          
          {/* Daily Sales Section - Bar Chart + Summary Table + Period Summary */}
          {dailySalesData.length > 0 && (
            <Card className="lg:col-span-3">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Vendas por Dia
                  </CardTitle>
                  <Link to={`/lojas/vendas?view=daily&store=${selectedStore === 'all' ? '' : selectedStore}&period=${period}`}>
                    <Button variant="ghost" size="sm">
                      Ver mais <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
                <CardDescription>
                  Evolução das vendas líquidas no período selecionado (barra = vendas diárias, linha = acumulado)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {/* Period Summary KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Total Líquido</p>
                    <p className="text-xl font-bold text-emerald-600">
                      {formatCurrency(dailySalesData.reduce((sum, d) => sum + (d.net_sales || 0), 0))}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Média Diária</p>
                    <p className="text-xl font-bold text-slate-700">
                      {formatCurrency(dailySalesData.length > 0 
                        ? dailySalesData.reduce((sum, d) => sum + (d.net_sales || 0), 0) / dailySalesData.length 
                        : 0)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Devoluções</p>
                    <p className="text-xl font-bold text-red-500">
                      {formatCurrency(dailySalesData.reduce((sum, d) => sum + (d.returns_value || 0), 0))}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Transações</p>
                    <p className="text-xl font-bold text-slate-700">
                      {dailySalesData.reduce((sum, d) => sum + (d.sales_documents || 0), 0).toLocaleString('pt-PT')}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Bar chart with cumulative line */}
                  <div className="lg:col-span-2">
                    <DailySalesBarChart 
                      data={dailySalesData}
                      onDayClick={handleDayDrillDown}
                    />
                  </div>
                  
                  {/* Summary table */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-slate-700">Últimos Dias</h4>
                      <span className="text-xs text-indigo-600">Acum.</span>
                    </div>
                    <DailySalesSummaryTable 
                      data={dailySalesData}
                      maxRows={10}
                      onDayClick={handleDayDrillDown}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Main content grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Store comparison (for "all" view) */}
            {selectedStore === 'all' && storeMetrics.length > 0 && (
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Comparação por Loja</CardTitle>
                  <CardDescription>Ranking de performance</CardDescription>
                </CardHeader>
                <CardContent>
                  <StoreComparisonTable stores={storeMetricsWithMargin} onStoreClick={handleStoreClick} getStoreName={getStoreName} />
                </CardContent>
              </Card>
            )}
            
            {/* Hourly analysis */}
            <Card className={selectedStore === 'all' && storeMetrics.length > 0 ? '' : 'lg:col-span-2'}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Vendas por Hora
                </CardTitle>
              </CardHeader>
              <CardContent>
                <HourlySalesChart data={hourlyData} />
              </CardContent>
            </Card>
            
            {/* Payment mix */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Mix de Pagamentos</CardTitle>
              </CardHeader>
              <CardContent>
                <PaymentMixChart 
                  cash={metrics.net_cash}
                  card={metrics.net_card}
                  other={metrics.net_other || 0}
                  total={metrics.total_payments_net || (metrics.net_cash + metrics.net_card + (metrics.net_other || 0))}
                />
              </CardContent>
            </Card>
            
            {/* Top products */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Top Produtos
                  </CardTitle>
                  <Link to="/lojas/analises?tab=produtos">
                    <Button variant="ghost" size="sm">
                      Ver todos <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </CardHeader>
              <CardContent>
                <TopSKUsTable skus={topSkus} />
              </CardContent>
            </Card>
          </div>
          
          {/* Quick links */}
          <div className="flex flex-wrap gap-3">
            <Link to={`/lojas/vendas?store=${selectedStore === 'all' ? '' : selectedStore}`}>
              <Button variant="outline" size="sm">
                <Receipt className="w-4 h-4 mr-2" />
                Ver Documentos
              </Button>
            </Link>
            <Link to="/lojas/analises?tab=devolucoes">
              <Button variant="outline" size="sm">
                <TrendingDown className="w-4 h-4 mr-2" />
                Análise Devoluções
              </Button>
            </Link>
            <Link to="/lojas/analises?tab=descontos">
              <Button variant="outline" size="sm">
                <Receipt className="w-4 h-4 mr-2" />
                Análise Descontos
              </Button>
            </Link>
          </div>
        </>
      )}
      
      {/* No data state - Enhanced */}
      {!loading && !error && (!metrics || !hasData) && (
        <Card>
          <CardContent className="py-12 text-center">
            <Store className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <DataQualityBadge status="NO_DATA" showLabel={true} />
            <h3 className="text-lg font-medium text-slate-700 mt-4">Sem dados disponíveis</h3>
            <p className="text-sm text-slate-500 mt-1">
              Não existem dados de vendas para o período selecionado.
            </p>
            <p className="text-xs text-slate-400 mt-2">
              Importe ficheiros XML WinTouch através da área de administração.
            </p>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
};

export default RetailDashboard;
